<template>
  <div class="chat border-radius-s flex-column flex-end overflow-hidden">
    <div v-if="!conversationUuid">
      <h3>{{ translateUcFirst("chat.message.noConversationTitle") }}</h3>
    </div>

    <div
      class="chat-header relative z-index-2 flex-middle gap-l padding bg-gray-2"
      v-if="showHeader && conversationUuid">
      <span class="block avatars">
        <div class="user-avatars relative">
          <chat-user-avatar v-for="profile in participants" v-bind="profile">
          </chat-user-avatar>
        </div>
      </span>
      <div class="">
        <span class="block text-l bold">{{ participantString }}</span>
        <span class="block" v-if="Object.values(participantRoles).length > 0">
          <span
            >{{ tu("chat.conversation.ui.rolesInConversation") }}:
            {{ participantsRoleString }}</span
          >
        </span>
      </div>
    </div>
    <div
      v-if="conversationUuid"
      ref="messages-wrapper"
      :style="{ maxHeight: maxHeight }"
      class="messages-wrapper relative z-index-1 bg-bg-4 flex-column flex-end overflow-y-auto max-size-parent">
      <spinner v-if="asyncStatus.asyncDataLoading" size="2"></spinner>
      <div
        v-if="messages && conversationUuid"
        class="messages flex-column gap-2xl flex-end size-parent padding-l-bottom"
        style="flex-direction: column-reverse">
        <chat-message
          v-if="messages"
          v-for="(message, index) in messages"
          :message="message"
          :show-sender-name="isGroupChat"
          :style="`z-index: ${messages.length - index}`"
          class="message animate__fadeIn animate--fast">
        </chat-message>
      </div>
    </div>

    <div v-if="conversationUuid && showSendControls" class="actions">
      <div class="flex gap-s flex-middle flex-no-wrap width-expand">
        <form-input
          v-model="messageText"
          class="message-input width-expand"
          :label="false"
          placeholder="chat.message.sendUi.contentPlaceholder"
          style="min-height: 0px"
          type="text"
          @keyup.enter="submitMessageByState()"></form-input>

        <form-button
          class="message-send-cta"
          icon-end="chevron-inline-end"
          :text="false"
          :show-slot="false"
          :slim="true"
          @click="submitMessageByState()"></form-button>
      </div>

    </div>
  </div>
</template>

<script>
import ChatUserAvatar from "@/client/components/chat/UserAvatar.vue";
import ChatMessage from "@/client/components/chat/ChatMessage.vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { computed } from "vue";
import useChat from "@/client/extensions/composition/useChat";

export default {
  name: "ChatComponent",
  components: {
    ChatMessage,
    ChatUserAvatar,
  },
  props: {
    maxHeight: {
      type: String,
      default: "inherit",
    },
    conversationUuid: {
      type: [String, Boolean],
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showSendControls: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus, socket } =
      asyncOperations(props);
    let { chat } = useChat();

    return {
      asyncOps,
      asyncOpsReady,
      asyncStatus,
      socket: socket.init(),
      chat,
    };
  },
  emits: ["chat:message-received", "chat:message-sent"],
  data: function () {
    const socketDebouncer = utilities.debounce(
      this.immediateSocketMessageHandler,
      200,
      {
        returnType: "all",
      }
    );

    return {
      messages: false,
      messageText: "",
      socketDebounce: socketDebouncer.debounce,
      socketDebounceClear: socketDebouncer.clear,
      roleIconMap: {
        admin: "shield-check",
        vendor: "cart",
        support: "user",
      },
      asyncData: {
        messages: {
          target: computed(
            () => `chat/conversation/${this.conversationUuid}/messages`
          ),
          data: {
            filters: {},
            pagination: {
              start: 0,
              size: 100,
            },
            ordering: { key: "createdAt", direction: "desc" },
          },
          shouldFetch: computed(
            () => typeof this.conversationUuid === "string"
          ),

          options: {
            cache: {
              enable: false,
            },
            dataMutator: (data) => {
              return data.items;
            },
          },
        },
      },
    };
  },
  computed: {
    participantString() {
      let result = [
        this.tu("chat.conversation.ui.myself"),
        ...Object.values(this.participantsOtherThanSelf).map(
          (user) => user.fullName
        ),
      ];

      return result.join(", ");
    },
    participants() {
      if (!this.messages) {
        return {};
      }
      let participants = {};
      this.messages.forEach((msg) => {
        if (!participants.hasOwnProperty(msg.Sender.uuid)) {
          participants[msg.Sender.uuid] = msg.Sender;
        }

        msg.Recipients.forEach((user) => {
          if (!participants.hasOwnProperty(user.uuid)) {
            participants[user.uuid] = user;
          }
        });
      });

      return participants;
    },
    participantsOtherThanSelf() {
      let result = {};
      let participants = this.participants;
      let ourUuid = this.sg("user.profile")?.uuid;
      for (const [key, value] of Object.entries(participants)) {
        if (key !== ourUuid) {
          result[key] = value;
        }
      }
      return result;
    },
    isGroupChat() {
      return Object.keys(this.participants).length > 2;
    },
    participantRoles() {
      if (!this.messages) {
        return {};
      }

      let result = {};

      let userUuid = this.sg("user.profile")?.uuid;
      this.messages.forEach((msg) => {
        if (userUuid && msg?.Sender?.uuid === userUuid) {
          return true;
        }
        result = { ...result, ...this.getMsgSenderRoles(msg) };
      });
      return result;
    },
    participantsRoleString() {
      if (!this.participantRoles) {
        return "";
      }
      return Object.values(this.participantRoles)
        .map((item) => this.tu(item.label))
        .join(", ");
    },
    finalConversationUuid() {
      return this.conversationUuid;
    },
    currentUserId() {
      let profile = this.$store.getters["user/profile"];
      if (!profile) {
        return 0;
      }
      return profile.id;
    },
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(this.scrollMessagesWrapperToEnd);
      },
      immediate: true,
      deep: true,
    },
    conversationUuid: {
      handler(newVal) {
        if (newVal) {
          this.refreshAsyncData();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.socket = await this.socket;
    await this.socket.waitForUUID();
    this.$nextTick(this.scrollMessagesWrapperToEnd);
    this.socket.on("newChatMessage", this.newSocketMessageHandler);
    this.socket.on("newMessageRead", this.newSocketMessageHandler);
  },
  beforeUnmount() {
    try {
      this.socket.off("newChatMessage", this.newSocketMessageHandler);
    } catch (e) {}
    try {
      this.socket.off("newMessageRead", this.newSocketMessageHandler);
    } catch (e) {}
    try {
      this.socketDebounceClear();
    } catch (e) {}
  },
  methods: {
    getMsgSenderRoles(msg) {
      let result = {};
      if (!Array.isArray(msg.Sender.roles) || msg.Sender.roles.length < 1) {
        return [];
      }
      msg.Sender.roles.forEach((role) => {
        result[role] = {
          type: role,
          label: `chat.user.roles.${role}`,
          icon: this.roleIconMap[role] ? this.roleIconMap[role] : false,
        };
      });

      return result;
    },
    async immediateSocketMessageHandler(data) {
      await this.refreshAsyncData();
      if (!utilities.isSSR()) {
        setTimeout(() => {
          this.$emit("chat:message-received", {
            conversationUuid: this.conversationUuid,
            messageData: data,
          });
        }, 100);
      }
    },
    newSocketMessageHandler(data) {
      if (!data.conversationUuid) {
        return;
      }

      if (data.conversationUuid != this.conversationUuid) {
        return;
      }

      this.socketDebounce();
    },
    isMessageFromSelf(message) {
      return message.Sender.uuid === this.$store.getters["user/profile"]?.uuid;
    },
    scrollMessagesWrapperToEnd() {
      if (utilities.isSSR()) {
        return;
      }
      setTimeout(() => {
        try {
          let el = this.$refs["messages-wrapper"];
          el.scrollTop = el.scrollHeight;
        } catch (e) {
          // console.log(e);
        }
      }, 100);
    },
    async submitMessageByState() {
      let result = await this.chat.sendMessage(
        this.finalConversationUuid,
        this.messageText
      );

      if (result.isError) {
        this.$s.ui.notification("chat.message.generalSendError", "error");
        return;
      }

      this.refreshAsyncData();
      this.clearMessage();
    },
    clearMessage() {
      this.messageText = "";
    },
  },
};
</script>

<style scoped lang="scss">
.chat {
  border-radius: var(--border-radius-s);
  display: flex;
  flex-direction: column;
  flex: 100%;
}

.chat-header {
  position: relative;
  z-index: var(--z-2);
  border-top-left-radius: var(--border-radius-s);
  border-top-right-radius: var(--border-radius-s);
  box-shadow: var(--box-shadow-2);

  .user-avatars {
    width: 2.5em;
    flex: 0 0 2.5em;
    height: 2.5em;
    position: relative;
    inset-inline-start: -2px;
    margin-inline-end: 1em;

    .user-avatar {
      position: absolute;
      top: 0;
      inset-inline-start: 0;
      z-index: 3;

      &:nth-child(1) {
        inset-inline-start: 0.7em;
        z-index: 2;
      }

      &:nth-child(2) {
        inset-inline-start: 1.4em;
        z-index: 1;
      }

      &:nth-child(n + 4) {
        display: none;
      }
    }
  }
}

.messages-wrapper {
  border: 1px solid var(--c-gray-2);

  border-top: none;
  flex: 100%;
  padding: var(--margin-l);
  border-bottom-left-radius: var(--border-radius-s);
  border-bottom-right-radius: var(--border-radius-s);

  @media (max-width: 1024px) {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  @media (max-width: 767px) {
    padding-inline: var(--margin-m);
  }
}

.messages {
  flex: 100%;
}

.chat :deep(.message:first-of-type) {
  .message-blob {
    margin-bottom: var(--margin-l);
  }
}

.actions {
  margin-top: 10px;
  padding: var(--margin-s) var(--marign-l);
  background: var(--c-bg-4);
}
</style>
